/*
 * ActivityForm.tsx : Un formulaire pour ajouter ou modifier une activité.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faChartLine,
	faClock,
	faCoins,
	faRightLeft,
	faArrowRight,
	faArrowLeft,
	faInfoCircle,
	faLink,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	DatePickerField,
	finishFormSave,
	FormSaveActions,
	InputField,
	RelatedItemsTable,
	SelectField,
	ToggleButtonField,
	useFormDirty,
} from '../../../common';
import CardHeader from '../../../layout/CardHeader';
import CardSection from '../../../layout/CardSection';

// Import Activity
import { ActivityData, TaxRateData } from '../types/types';
import { activitiesApi } from '../services/activitiesApi';
import { isActivityDay } from '../services/activities';
import { servicesApi } from 'features/services/services/servicesApi';
import { projectsApi } from 'features/projects/services/projectsApi';
import { customerInvoicesApi } from 'features/customerInvoices/services/customerInvoicesApi';
import { creditNotesApi } from 'features/creditNotes/services/creditNotesApi';
import { taxRatesApi } from 'features/taxRate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ServiceData } from 'features/services';
import { BadgeMovementData } from 'features/badgeMovement';

// Composant ActivityForm
const ActivityForm: React.FC = () => {
	// Hooks de Chargement
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);

	// Hooks pour l'utilisateur courant
	const userId = localStorage.getItem('userId');
	const userName = localStorage.getItem('name');
	const userFirstName = localStorage.getItem('firstName');

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();

	// Hooks pour le formulaire courant
	const [formData, setFormData] = React.useState<ActivityData>({
		id: 0,
		beginDate: new Date(),
		endDate: null,
		isDay: true,
		origin: 'MANUAL',
		service: null,
		project: null,
		customerInvoice: null,
		creditNote: null,
		createdBy: userId
			? {
					id: Number(userId),
					name: userName || '',
					firstName: userFirstName || '',
				}
			: undefined,
		createdAt: new Date()
			.toISOString()
			.replace('Z', '+00:00') as unknown as Date,
	});
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Hooks select origin
	const [originOptions] = React.useState<{ key: string; label: string }[]>([
		{ key: 'BADGE', label: 'Badge' },
		{ key: 'MANUAL', label: 'Manuelle' },
	]);

	// Hooks select service
	const [serviceOptions, setServiceOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [services, setServices] = React.useState<ServiceData[]>([]);
	const [loadingServices, setLoadingServices] = React.useState(true);

	// Hooks select project
	const [projectOptions, setProjectOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingProjects, setLoadingProjects] = React.useState(true);

	// Hooks select customerInvoice
	const [customerInvoiceOptions, setCustomerInvoiceOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingCustomerInvoices, setLoadingCustomerInvoices] =
		React.useState(true);

	// Hooks select creditNote
	const [creditNoteOptions, setCreditNoteOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingCreditNotes, setLoadingCreditNotes] = React.useState(true);

	// Hooks select taxRate
	const [taxRateOptions, setTaxRateOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [taxRates, setTaxRates] = React.useState<TaxRateData[]>([]);
	const [loadingTaxRates, setLoadingTaxRates] = React.useState(true);

	// Hooks automatique (recalculer le tarif TTC à partir du tarif HT et du taux de TVA)
	const unitPriceInclTax = React.useMemo(() => {
		if (!formData.unitPriceExclTax) return 0;
		// TTC = HT * (1 + taux)
		return (
			Number(formData.unitPriceExclTax) * (1 + (formData.taxRate?.rate || 0))
		);
	}, [formData.unitPriceExclTax, formData.taxRate?.rate]);

	// Loading : Fetch des données et remplissage du formulaire (une fois le composant monté, si un id est présent)
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Vérifier la présence d'un id (édition) ou non (création)
		if (id) {
			activitiesApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
					// Préremplir les selects
					if (data.service) {
						setServiceOptions([
							{
								key: data.service.id.toString(),
								label: data.service.label,
							},
						]);
					}
					if (data.project) {
						setProjectOptions([
							{
								key: data.project.id.toString(),
								label: data.project.referenceNumber,
							},
						]);
					}
					if (data.customerInvoice) {
						setCustomerInvoiceOptions([
							{
								key: data.customerInvoice.id.toString(),
								label: data.customerInvoice.referenceNumber,
							},
						]);
					}
					if (data.creditNote) {
						setCreditNoteOptions([
							{
								key: data.creditNote.id.toString(),
								label: data.creditNote.referenceNumber,
							},
						]);
					}
					if (data.taxRate) {
						setTaxRateOptions([
							{
								key: data.taxRate.id.toString(),
								label: data.taxRate.label || data.taxRate.code,
							},
						]);
					}
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Lazy loading : options services en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingServices) {
			servicesApi
				.listPaginated(token, { limit: 999999 })
				.then((res) => {
					setServiceOptions(
						res.data.map((service) => ({
							key: service.id.toString(),
							label: service.label,
						}))
					);
					setServices(res.data);
				})
				.catch(console.error)
				.finally(() => setLoadingServices(false));
		}
	}, [loading, loadingServices]);

	// Lazy loading : options projets en arrière-plan (route /select allégée)
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingProjects) {
			projectsApi
				.listSelect(token)
				.then((items) => {
					setProjectOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingProjects(false));
		}
	}, [loading, loadingProjects]);

	// Lazy loading : options customerInvoices en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingCustomerInvoices) {
			customerInvoicesApi
				.listSelect(token)
				.then((items) => {
					setCustomerInvoiceOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingCustomerInvoices(false));
		}
	}, [loading, loadingCustomerInvoices]);

	// Lazy loading : options creditNotes en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingCreditNotes) {
			creditNotesApi
				.listSelect(token)
				.then((items) => {
					setCreditNoteOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingCreditNotes(false));
		}
	}, [loading, loadingCreditNotes]);

	// Lazy loading : options taxRates en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingTaxRates) {
			taxRatesApi
				.listPaginated(token, { limit: 999999 })
				.then((res) => {
					setTaxRateOptions(
						res.data.map((taxRate) => ({
							key: taxRate.id.toString(),
							label: taxRate.label || taxRate.code,
						}))
					);
					setTaxRates(res.data);
				})
				.catch(console.error)
				.finally(() => setLoadingTaxRates(false));
		}
	}, [loading, loadingTaxRates]);

	// Handle Formulaire : Soumission
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/activities');
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			activitiesApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			activitiesApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/activities/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Handle Formulaire : effacer les selects liés
	const handleClearCustomerInvoice = () => {
		setFormData((prev) => ({
			...prev,
			customerInvoice: null,
		}));
	};
	const handleClearCreditNote = () => {
		setFormData((prev) => ({
			...prev,
			creditNote: null,
		}));
	};

	// Handle Formulaire : Changements des inputs
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Handle Formulaire : Changements des selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'origin') {
			setFormData((prev) => ({
				...prev,
				origin: value,
			}));
			return;
		}

		if (name === 'service') {
			const selectedOption = serviceOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				const service = services.find(
					(s) => s.id === Number(selectedOption.key)
				);
				const defaultPrice = service ? service.defaultPrice || 0.0 : 0.0;
				const defaultTaxRate = service
					? service.defaultTaxRate || formData.taxRate
					: formData.taxRate;
				const taxRateInfo = defaultTaxRate
					? taxRates.find((rate) => rate.code === defaultTaxRate.code)
					: undefined;
				setFormData((prev) => ({
					...prev,
					service: service || prev.service,
					unitPriceExclTax: defaultPrice,
					taxRate: taxRateInfo
						? {
								id: taxRateInfo.id,
								code: taxRateInfo.code,
								label: taxRateInfo.label,
								rate: taxRateInfo.rate,
								country: taxRateInfo.country,
								isActive: taxRateInfo.isActive,
							}
						: undefined,
				}));
			}
			return;
		}

		if (name === 'project') {
			const selectedOption = projectOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					project: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'customerInvoice') {
			const selectedOption = customerInvoiceOptions.find(
				(opt) => opt.key === value
			);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					customerInvoice: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'creditNote') {
			const selectedOption = creditNoteOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					creditNote: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'taxRate') {
			const selectedOption = taxRateOptions.find((opt) => opt.key === value);
			// On récupère toutes les infos dans taxRates pour pouvoir remplir correctement le formData (notamment le taux de TVA)
			if (selectedOption) {
				const matched = taxRates.find(
					(rate) => (rate.label || rate.code) === selectedOption.label
				);
				setFormData((prev) => ({
					...prev,
					taxRate: {
						id: Number(selectedOption.key),
						code: matched?.code || '',
						label: selectedOption.label,
						rate: matched?.rate || 0,
						country: matched?.country || '',
						isActive: matched?.isActive || false,
					},
				}));
			}
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Handle Formulaire : Changement de isDay lorsque une date de début ou de fin est modifiée
	const handleDateChange = (
		field: 'beginDate' | 'endDate',
		date: Date | string | null
	) => {
		if (!date) return;

		// on force un objet Date
		const newDate = date instanceof Date ? date : new Date(date);

		const newFormData = {
			...formData,
			[field]: newDate,
			// on convertit aussi les autres dates si elles sont des strings
			beginDate:
				field === 'beginDate'
					? newDate
					: formData.beginDate instanceof Date
						? formData.beginDate
						: new Date(formData.beginDate || ''),
			endDate:
				field === 'endDate'
					? newDate
					: formData.endDate instanceof Date
						? formData.endDate
						: new Date(formData.endDate || ''),
		};

		// recalcul de isDay si les deux dates sont définies
		if (newFormData.beginDate && newFormData.endDate) {
			newFormData.isDay = isActivityDay(
				newFormData.beginDate,
				newFormData.endDate
			);
		}

		setFormData(newFormData);
	};

	// Render : Rendu du composant, le premier div est la card principale
	return (
		<>
			<div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-6">
				{/* HEADER DE LA CARD */}
				<CardHeader
					icon={faChartLine}
					title={id ? "Édition d'une activité" : 'Nouvelle activité'}
					createdAt={
						formData.createdAt
							? formData.createdAt?.toLocaleString()
							: undefined
					}
					createdBy={
						formData.createdBy
							? formData.createdBy.firstName + ' ' + formData.createdBy.name
							: undefined
					}
					loading={loading}
				/>

				{/* CONTENU DE LA CARD */}
				<form onSubmit={handleSubmit} className="space-y-6">
					{/* 2 COLONNES */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* COLONNE */}
						<CardSection icon={faInfoCircle} title="Informations générales">
							<SelectField
								label="Origine"
								selectedKey={formData.origin || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'origin',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={originOptions}
								loading={loading}
							/>
							<SelectField
								label="Service"
								selectedKey={formData.service?.id?.toString() || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'service',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={serviceOptions}
								loading={loading}
								optionsLoading={loadingServices}
								entityHref={
									formData.service?.id
										? `/app/settings/services/${formData.service.id}/edit`
										: undefined
								}
								onClear={() =>
									setFormData((prev) => ({
										...prev,
										service: null,
									}))
								}
							/>
							<SelectField
								label="Affaire"
								selectedKey={formData.project?.id?.toString() || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'project',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={projectOptions}
								loading={loading}
								optionsLoading={loadingProjects}
								entityHref={
									formData.project?.id
										? `/app/projects/${formData.project.id}/edit`
										: undefined
								}
								onClear={() =>
									setFormData((prev) => ({
										...prev,
										project: null,
									}))
								}
							/>
						</CardSection>

						{/* COLONNE */}
						<CardSection icon={faCoins} title="Budget">
							<SelectField
								label="Taux de TVA"
								selectedKey={formData.taxRate?.id?.toString() || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'taxRate',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={taxRateOptions}
								loading={loading}
								optionsLoading={loadingTaxRates}
								entityHref={
									formData.taxRate?.id
										? `/app/taxrates/${formData.taxRate.id}/edit`
										: undefined
								}
								onClear={() =>
									setFormData((prev) => ({
										...prev,
										taxRate: null,
									}))
								}
							/>
							<InputField
								label="Tarif unitaire HT"
								name="unitPriceExclTax"
								type="number"
								value={formData.unitPriceExclTax || 0}
								onChange={handleChange}
								loading={loading}
								placeholder='ex: "0.0"'
							/>
							<InputField
								label="Tarif unitaire TTC"
								name="unitPriceInclTax"
								type="number"
								value={unitPriceInclTax.toFixed(2)}
								onChange={handleChange}
								loading={loading}
								placeholder="automatique"
								readOnly
							/>
						</CardSection>

						{/* COLONNE */}
						<CardSection icon={faClock} title="Dates et périodicité">
							<DatePickerField
								label="Date de début"
								name="beginDate"
								value={
									formData.beginDate ? new Date(formData.beginDate) : undefined
								}
								onChange={(date) => handleDateChange('beginDate', date || null)}
								loading={loading}
								showTime={true}
							/>
							<DatePickerField
								label="Date de fin"
								name="endDate"
								value={
									formData.endDate ? new Date(formData.endDate) : undefined
								}
								onChange={(date) => handleDateChange('endDate', date || null)}
								loading={loading}
								showTime={true}
							/>
							<ToggleButtonField
								label="De nuit"
								isOn={!formData.isDay || false}
								onChange={(value) =>
									setFormData({ ...formData, isDay: !value })
								}
								loading={loading}
							/>
						</CardSection>

						{/* COLONNE */}
						<CardSection icon={faLink} title="Liens internes">
							{/* 2 colonnes pour les selects + boutons de nettoyage alignés à droite */}
							<SelectField
								label="Facture client (si activité facturée)"
								selectedKey={formData.customerInvoice?.id?.toString() || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'customerInvoice',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={customerInvoiceOptions}
								loading={loading}
								optionsLoading={loadingCustomerInvoices}
								entityHref={
									formData.customerInvoice?.id
										? `/app/customerInvoices/${formData.customerInvoice.id}/edit`
										: undefined
								}
								onClear={handleClearCustomerInvoice}
							/>
							<SelectField
								label="Avoir (si activité remboursée)"
								selectedKey={formData.creditNote?.id?.toString() || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'creditNote',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={creditNoteOptions}
								loading={loading}
								optionsLoading={loadingCreditNotes}
								entityHref={
									formData.creditNote?.id
										? `/app/creditNotes/${formData.creditNote.id}/edit`
										: undefined
								}
								onClear={handleClearCreditNote}
							/>
						</CardSection>
					</div>
				</form>

				<FormSaveActions
					isDirty={isDirty}
					isSubmitting={isSubmitting}
					onCancel={() => navigate('/app/activities')}
					onSave={() => handleSave(false)}
					onSaveAndLeave={() => handleSave(true)}
				/>
			</div>

			{/* LIENS EXTERNES — informations en lecture seule liées à cette activité,
		    gérées depuis d'autres modules (mouvements de badge, etc.)
		    Hors du div principal : card indépendante, espacée, sans fond blanc partagé. */}
			{id && (
				<div className="mt-6 max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md">
					{/* Mouvements de badge associés à cette activité */}
					<RelatedItemsTable<BadgeMovementData>
						title="Mouvements de badges associés"
						items={formData.badgeMovements ?? []}
						columns={[
							{
								key: 'id',
								label: 'ID',
								render: (_val, item) =>
									item?.id ? (
										<a
											href={`/app/badgeMovements/${item.id}/edit`}
											className="text-gray-500 underline hover:text-gray-700"
											onClick={(e) => {
												// si clic normal (left click) on utilise navigate pour rester en SPA
												if (!e.ctrlKey && !e.metaKey && e.button === 0) {
													e.preventDefault();
													navigate(`/app/badgeMovements/${item.id}/edit`);
												}
												// sinon (middle-click ou ctrl/cmd+click), laisser le comportement par défaut (nouvel onglet)
											}}
										>
											{item.id}
										</a>
									) : (
										'—'
									),
							},
							{
								key: 'direction',
								label: 'Direction',
								render: (val) =>
									val === 'IN' ? (
										<FontAwesomeIcon
											icon={faArrowRight}
											style={{ color: 'green' }}
										/>
									) : val === 'OUT' ? (
										<FontAwesomeIcon
											icon={faArrowLeft}
											style={{ color: 'red' }}
										/>
									) : (
										'—'
									),
							},
							{
								key: 'dateTime',
								label: 'Date et heure',
								render: (val) =>
									val ? new Date(val as string).toLocaleString('fr-FR') : '—',
							},
							{
								key: 'ownerFirstName',
								label: 'Prénom',
								render: (val) => (val as string) || '—',
							},
							{
								key: 'ownerLastName',
								label: 'Nom',
								render: (val) => (val as string) || '—',
							},
							{
								key: 'badgeNumber',
								label: 'Badge',
								render: (val) => (val as string) || '—',
							},
						]}
						actionIcon={faRightLeft}
						actionColorClass=""
						onAction={() => {
							/* lecture seule */
						}}
						readOnly
						readOnlyTooltip="Les mouvements de badge sont gérés depuis le module Badges."
					/>
				</div>
			)}
		</>
	);
};

export default ActivityForm;
